import { z } from "zod";

import { TeamsUserRoles } from "../../hooks/useAccountTypes";
import type { BillingDetails } from "../../hooks/useCheckout";
import { ChargebeePaymentType, PaymentTypes } from "../../lib/ChargebeeAdapter";
import messages from "../../locales/messages";
import { ServiceErrorCode, SubscriptionPeriod } from "../../types";
import type {
  CreditCardInputError,
  CreditCardInputValue,
} from "../CreditCardInput/CreditCardInputTypes";

export const COUNTRIES_WITH_MANDATORY_STATE_SELECTOR = ["US", "CA", "IN", "BR"];
export const MANDATORY_BILLING_DATA_COUNTRIES = ["US", "BR", "CA", "IN"];
export const MANDATORY_BILLING_DATA_FIELDS: (keyof BillingDetails)[] = [
  "company",
  "zip",
  "city",
  "countryCode",
  "line1",
];

export type PaymentErrorMessageKey =
  | "declined"
  | "insufficientFunds"
  | "verificationFailed"
  | "generic";

export const mapChargebeeErrorMessages = (
  error: string
):
  | {
      titleMessage: typeof messages.Checkout.PaymentError.DeclinedTitle;
      descriptionMessage: typeof messages.Checkout.PaymentError.DeclinedDescription;
    }
  | undefined => {
  if (error === ServiceErrorCode.InvalidCoupon) {
    return {
      titleMessage: messages.Checkout.PaymentError.CouponInvalidTitle,
      descriptionMessage: messages.Checkout.PaymentError.CouponInvalidDescription,
    };
  }
  if (error === "CVC Declined" || error.includes("incorrect_cvc")) {
    return undefined;
  }
  if (
    error === "Refused" ||
    error.includes("Your card was declined") ||
    error.includes("Your card has been declined")
  ) {
    return {
      titleMessage: messages.Checkout.PaymentError.DeclinedTitle,
      descriptionMessage: messages.Checkout.PaymentError.DeclinedDescription,
    };
  }
  if (error === "Not enough balance" || error.includes("Your card has insufficient funds")) {
    return {
      titleMessage: messages.Checkout.PaymentError.InsufficientFundsTitle,
      descriptionMessage: messages.Checkout.PaymentError.InsufficientFundsDescription,
    };
  }
  if (
    error === "3D Not Authenticated" ||
    error.includes("We are unable to authenticate your payment method")
  ) {
    return {
      titleMessage: messages.Checkout.PaymentError.VerificationFailedTitle,
      descriptionMessage: messages.Checkout.PaymentError.VerificationFailedDescription,
    };
  }
  return {
    titleMessage: messages.Checkout.PaymentError.GenericTitle,
    descriptionMessage: messages.Checkout.PaymentError.GenericDescription,
  };
};

const mapCreditCardErrorMessages = (error: CreditCardInputError) => {
  switch (error.errorCode) {
    case "card_number_incomplete":
      return messages.Checkout.ValidationError.IncompleteCardNumber.defaultMessage;
    case "card_number_invalid":
      return messages.Checkout.ValidationError.InvalidCardNumber.defaultMessage;
    case "card_cvv_invalid":
      return messages.Checkout.ValidationError.InvalidCvv.defaultMessage;
    default:
      return error.message;
  }
};

export const schema = z
  .object({
    editorSeatCount: z.number().min(1).max(50),
    inviteEmails: z.array(z.string()),
    role: z.enum([TeamsUserRoles.Editor, TeamsUserRoles.Contributor]).optional(),
    paymentType: z.enum(PaymentTypes),
    card: z.custom<CreditCardInputValue>().optional(),
    period: z.nativeEnum(SubscriptionPeriod),
    company: z.string().optional(),
    countryCode: z.string().optional(),
    stateCode: z.string().optional(),
    city: z.string().optional(),
    zip: z.string().optional(),
    line1: z.string().optional(),
    line2: z.string().optional(),
    vatNumber: z.string().optional(),
    couponCode: z.string().optional(),
    migrationConsent: z.boolean().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.paymentType !== ChargebeePaymentType.Card) {
      return;
    }

    if (!val.card) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        type: "string",
        minimum: 1,
        inclusive: true,
        exact: false,
        message: messages.Checkout.ValidationError.Required.defaultMessage,
        path: ["card"],
      });

      return;
    }

    if (val.card.error) {
      ctx.addIssue({
        code: "custom",
        message: mapCreditCardErrorMessages(val.card?.error),
        path: ["card"],
      });

      return;
    }
  })
  .superRefine((val, ctx) => {
    if (!val.countryCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        type: "string",
        minimum: 1,
        inclusive: true,
        exact: false,
        message: messages.Checkout.ValidationError.Required.defaultMessage,
        path: ["countryCode"],
      });
    }
  })
  .superRefine((val, ctx) => {
    const country = val.countryCode;
    if (country && COUNTRIES_WITH_MANDATORY_STATE_SELECTOR.includes(country) && !val.stateCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: messages.Checkout.ValidationError.Required.defaultMessage,
        path: ["stateCode"],
      });
    }
  })
  .superRefine((val, ctx) => {
    const country = val.countryCode;
    if ((country && MANDATORY_BILLING_DATA_COUNTRIES.includes(country)) || val.vatNumber) {
      const missingBillingDetailsFields = MANDATORY_BILLING_DATA_FIELDS.filter(
        (fieldName) => !val[fieldName]
      );
      missingBillingDetailsFields.forEach((fieldName) => {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: messages.Checkout.ValidationError.Required.defaultMessage,
          path: [fieldName],
        });
      });
    }
  });

export type CheckoutFormData = z.infer<typeof schema>;
